import { useCallback, useEffect, useMemo, useState } from 'react';
import { Droplets, Loader2, Trash2, Wand2 } from 'lucide-react';
import irrigationNetworkService, {
  type AreaIrrigation,
  type InstallSpecInput,
  type IrrigationNode,
  type IrrigationSystemType,
  type ValveDemandRow,
} from '../../../services/irrigationNetwork';
import {
  computeInstallFlow, fieldsFor, missingRequiredField,
  type DerivedFlow,
} from '../../../utils/irrigationFlow';
import { errorMessage } from '../../../utils/apiResponse';
import { toNum } from '@/utils/numeric';

interface Props {
  node: IrrigationNode;
  editable: boolean;
  /** Called after a save or an applied flow, so the drawer reloads the node. */
  onNodeChanged: () => void | Promise<void>;
}

/** Form state is all strings — an in-progress number must survive as typed. */
type Draft = {
  system_type_key: string;
  emitter_flow_lph: string;
  emitter_model: string;
  emitter_spacing_m: string;
  lateral_spacing_m: string;
  laterals_per_row: string;
  emitters_per_plant: string;
  flow_lph_per_ha: string;
  design_pressure_kpa: string;
  installed_year: string;
  notes: string;
};

const EMPTY: Draft = {
  system_type_key: '', emitter_flow_lph: '', emitter_model: '',
  emitter_spacing_m: '', lateral_spacing_m: '', laterals_per_row: '1',
  emitters_per_plant: '', flow_lph_per_ha: '', design_pressure_kpa: '',
  installed_year: '', notes: '',
};

const str = (v: unknown) => (v === null || v === undefined ? '' : String(v));
const orNull = (v: string) => (v.trim() === '' ? null : Number(v));

/**
 * What is installed in the planting a node sits in, and what it therefore draws.
 *
 * The spec lives on the cultivar area rather than on the valve or its drawn zone
 * because the cultivar area is the only entity that knows its own hectares, row
 * spacing and tree count — and for orchard drip the lateral spacing IS the row
 * spacing, so that is where the arithmetic closes without anyone measuring
 * anything twice.
 *
 * Three things here are deliberate:
 *
 * - **The totals update as you type, from a client copy of the arithmetic**
 *   (`utils/irrigationFlow`), but the figures shown after a save are the
 *   server's. A form that reveals its answer a round trip after each keystroke
 *   does not get finished.
 * - **The valve's own share comes from the server, never computed here.**
 *   Splitting a planting between valves is the rule that silently triples a
 *   pump's duty if it goes wrong, so it has exactly one implementation.
 * - **A derived flow is applied by a button, never on save.** The figure already
 *   on the valve may have been measured with a flow meter, which beats anything
 *   this can work out.
 */
export default function IrrigationInstallPanel({ node, editable, onNodeChanged }: Props) {
  const areaId = node.cultivar_area_id;

  const [types, setTypes] = useState<IrrigationSystemType[]>([]);
  const [loaded, setLoaded] = useState<AreaIrrigation | null>(null);
  const [demand, setDemand] = useState<ValveDemandRow | null>(null);
  const [draft, setDraft] = useState<Draft>(EMPTY);
  const [loading, setLoading] = useState(false);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [loadFailed, setLoadFailed] = useState(false);

  useEffect(() => {
    let cancelled = false;
    irrigationNetworkService.listSystemTypes()
      // `?? []` rather than a bare assignment: an empty or malformed catalogue
      // response must leave the select with nothing to offer, not crash the
      // whole drawer on a `.map` of null.
      .then((t) => { if (!cancelled) setTypes(t ?? []); })
      .catch(() => { if (!cancelled) setTypes([]); });
    return () => { cancelled = true; };
  }, []);

  const load = useCallback(async () => {
    if (areaId === null) { setLoaded(null); setDemand(null); return; }
    setLoading(true);
    setLoadFailed(false);
    try {
      const data = await irrigationNetworkService.getAreaIrrigation(areaId);
      // An empty response is a failed read, not a planting with no system: the
      // difference is "we could not tell you" versus "nothing is installed", and
      // only one of those should invite somebody to fill the form in.
      if (!data || !data.cultivar_area) throw new Error('empty response');
      setLoaded(data);
      setDraft(data.spec ? {
        system_type_key: data.spec.system_type_key,
        emitter_flow_lph: str(data.spec.emitter_flow_lph),
        emitter_model: str(data.spec.emitter_model),
        emitter_spacing_m: str(data.spec.emitter_spacing_m),
        lateral_spacing_m: str(data.spec.lateral_spacing_m),
        laterals_per_row: str(data.spec.laterals_per_row ?? 1),
        emitters_per_plant: str(data.spec.emitters_per_plant),
        flow_lph_per_ha: str(data.spec.flow_lph_per_ha),
        design_pressure_kpa: str(data.spec.design_pressure_kpa),
        installed_year: str(data.spec.installed_year),
        notes: str(data.spec.notes),
      } : EMPTY);
    } catch {
      setLoaded(null);
      setLoadFailed(true);
    } finally { setLoading(false); }

    // A valve's share of the planting depends on its siblings, so it is read
    // from the server rather than worked out from the network in hand.
    if (node.node_type === 'valve') {
      try {
        const d = await irrigationNetworkService.getValveDemand(node.farm_id);
        setDemand(d?.valves?.find(v => v.node_id === node.id) ?? null);
      } catch { setDemand(null); }
    }
  }, [areaId, node.farm_id, node.id, node.node_type]);

  useEffect(() => { void load(); }, [load]);

  const selectedType = useMemo(
    () => types.find(t => t.key === draft.system_type_key) ?? null,
    [types, draft.system_type_key],
  );

  const specInput = useMemo<InstallSpecInput | null>(() => {
    if (!draft.system_type_key) return null;
    return {
      system_type_key: draft.system_type_key,
      emitter_flow_lph: orNull(draft.emitter_flow_lph),
      emitter_model: draft.emitter_model.trim() || null,
      emitter_spacing_m: orNull(draft.emitter_spacing_m),
      lateral_spacing_m: orNull(draft.lateral_spacing_m),
      laterals_per_row: orNull(draft.laterals_per_row) ?? 1,
      emitters_per_plant: orNull(draft.emitters_per_plant),
      flow_lph_per_ha: orNull(draft.flow_lph_per_ha),
      design_pressure_kpa: orNull(draft.design_pressure_kpa),
      installed_year: orNull(draft.installed_year),
      notes: draft.notes.trim() || null,
    };
  }, [draft]);

  /**
   * Preview while typing; the server's own figures once nothing has been
   * touched, so a saved spec is never displayed through a second implementation.
   */
  const preview: DerivedFlow | null = useMemo(() => {
    if (!selectedType || !specInput || !loaded) return null;
    return computeInstallFlow(specInput, selectedType, loaded.cultivar_area);
  }, [selectedType, specInput, loaded]);

  const blocker = selectedType && specInput
    ? missingRequiredField(specInput, selectedType)
    : 'Choose what is installed in this planting.';

  const save = async () => {
    if (areaId === null || !specInput || blocker) return;
    setBusy(true);
    setError(null);
    try {
      await irrigationNetworkService.saveAreaIrrigation(areaId, specInput);
      await load();
      // The valve's derived flow may now differ from what it claims, and the
      // drawer shows both.
      await onNodeChanged();
    } catch (e) {
      setError(errorMessage(e, 'Could not save the irrigation system'));
    } finally { setBusy(false); }
  };

  const remove = async () => {
    if (areaId === null) return;
    if (!confirm('Remove the irrigation system recorded for this planting? Valve flows already applied from it are left alone.')) return;
    setBusy(true);
    setError(null);
    try {
      await irrigationNetworkService.deleteAreaIrrigation(areaId);
      await load();
      await onNodeChanged();
    } catch (e) {
      setError(errorMessage(e, 'Could not remove the irrigation system'));
    } finally { setBusy(false); }
  };

  const applyFlow = async () => {
    setBusy(true);
    setError(null);
    try {
      await irrigationNetworkService.applyDerivedFlow(node.id);
      await load();
      await onNodeChanged();
    } catch (e) {
      setError(errorMessage(e, 'Could not apply the derived flow'));
    } finally { setBusy(false); }
  };

  if (areaId === null) {
    return (
      <Shell>
        <div className="text-gray-500">
          Attribute this node to a cultivar area above, and you can describe what
          is installed in it — and have a valve&rsquo;s flow worked out from the
          emitters rather than typed.
        </div>
      </Shell>
    );
  }

  const f = selectedType ? fieldsFor(selectedType.counting_method) : null;
  const area = loaded?.cultivar_area ?? null;

  return (
    <Shell>
      {loading && (
        <div className="text-gray-500 flex items-center gap-1">
          <Loader2 className="w-3 h-3 animate-spin" /> Loading&hellip;
        </div>
      )}
      {loadFailed && (
        <div className="text-amber-700">
          Could not read this planting&rsquo;s irrigation system.
        </div>
      )}

      {!loading && loaded && (
        <>
          <div className="text-gray-600">
            {area?.name}
            {toNum(area?.area_hectares) !== null && <> &middot; {toNum(area?.area_hectares)} ha</>}
            {toNum(area?.row_spacing_meters) !== null && <> &middot; {toNum(area?.row_spacing_meters)} m rows</>}
          </div>

          <label className="block">
            <span className="text-gray-600">Installed system</span>
            <select
              className="input mt-0.5"
              disabled={!editable || busy}
              value={draft.system_type_key}
              onChange={(e) => {
                const key = e.target.value;
                const t = types.find(x => x.key === key) ?? null;
                setDraft(d => ({
                  ...d,
                  system_type_key: key,
                  // A sensible starting output for the type, only into an empty
                  // field — overwriting a measured figure on a type change
                  // would lose it silently.
                  emitter_flow_lph: d.emitter_flow_lph
                    || str(t?.default_emitter_flow_lph ?? ''),
                }));
              }}
            >
              <option value="">— nothing recorded —</option>
              {types.map(t => (
                <option key={t.key} value={t.key}>{t.display_name}</option>
              ))}
            </select>
          </label>

          {selectedType?.notes && (
            <div className="text-[11px] text-gray-500">{selectedType.notes}</div>
          )}

          {f && (
            <>
              {f.emitterFlow && (
                <div className="grid grid-cols-2 gap-2">
                  <Field label="Emitter output (L/h)">
                    <input className="input" type="number" step="0.1" min="0"
                      disabled={!editable || busy}
                      value={draft.emitter_flow_lph}
                      onChange={(e) => setDraft(d => ({ ...d, emitter_flow_lph: e.target.value }))} />
                  </Field>
                  <Field label="Product (optional)">
                    <input className="input" disabled={!editable || busy}
                      placeholder="e.g. UniRam 2.0"
                      value={draft.emitter_model}
                      onChange={(e) => setDraft(d => ({ ...d, emitter_model: e.target.value }))} />
                  </Field>
                </div>
              )}

              {f.gridSpacing && (
                <>
                  <div className="grid grid-cols-2 gap-2">
                    <Field label="Emitter spacing (m)">
                      <input className="input" type="number" step="0.05" min="0"
                        disabled={!editable || busy}
                        value={draft.emitter_spacing_m}
                        onChange={(e) => setDraft(d => ({ ...d, emitter_spacing_m: e.target.value }))} />
                    </Field>
                    <Field label="Lines per row">
                      <select className="input" disabled={!editable || busy}
                        value={draft.laterals_per_row}
                        onChange={(e) => setDraft(d => ({ ...d, laterals_per_row: e.target.value }))}>
                        {[1, 2, 3, 4].map(n => <option key={n} value={n}>{n}</option>)}
                      </select>
                    </Field>
                  </div>
                  <Field label="Lateral spacing (m) — blank uses the row spacing">
                    <input className="input" type="number" step="0.1" min="0"
                      disabled={!editable || busy}
                      placeholder={
                        toNum(area?.row_spacing_meters) !== null
                          ? `${toNum(area?.row_spacing_meters)} ÷ ${draft.laterals_per_row || 1} from the planting`
                          : 'no row spacing on this planting'
                      }
                      value={draft.lateral_spacing_m}
                      onChange={(e) => setDraft(d => ({ ...d, lateral_spacing_m: e.target.value }))} />
                  </Field>
                </>
              )}

              {f.perPlant && (
                <Field label="Emitters per tree">
                  <input className="input" type="number" step="0.5" min="0"
                    disabled={!editable || busy}
                    value={draft.emitters_per_plant}
                    onChange={(e) => setDraft(d => ({ ...d, emitters_per_plant: e.target.value }))} />
                </Field>
              )}

              {f.ratePerHa && (
                <Field label="Delivery rate (L/h per ha)">
                  <input className="input" type="number" step="100" min="0"
                    disabled={!editable || busy}
                    value={draft.flow_lph_per_ha}
                    onChange={(e) => setDraft(d => ({ ...d, flow_lph_per_ha: e.target.value }))} />
                </Field>
              )}

              <div className="grid grid-cols-2 gap-2">
                <Field label="Design pressure (kPa)">
                  <input className="input" type="number" step="10" min="0"
                    disabled={!editable || busy}
                    value={draft.design_pressure_kpa}
                    onChange={(e) => setDraft(d => ({ ...d, design_pressure_kpa: e.target.value }))} />
                </Field>
                <Field label="Installed (year)">
                  <input className="input" type="number" step="1" min="1900" max="2100"
                    disabled={!editable || busy}
                    value={draft.installed_year}
                    onChange={(e) => setDraft(d => ({ ...d, installed_year: e.target.value }))} />
                </Field>
              </div>

              {preview && <DerivedSummary derived={preview} />}

              {editable && (
                <div className="flex flex-wrap items-center gap-2">
                  <button
                    className="px-2 py-1 rounded bg-blue-600 text-white disabled:opacity-40"
                    disabled={busy || blocker !== null}
                    onClick={save}
                  >
                    {busy ? <Loader2 className="w-3 h-3 animate-spin" /> : 'Save system'}
                  </button>
                  {loaded.spec && (
                    <button className="text-red-600 flex items-center gap-1"
                      disabled={busy} onClick={remove}>
                      <Trash2 className="w-3 h-3" /> remove
                    </button>
                  )}
                  {blocker && <span className="text-amber-700">{blocker}</span>}
                </div>
              )}
            </>
          )}

          {node.node_type === 'valve' && (
            <ValveShare
              node={node}
              demand={demand}
              editable={editable}
              busy={busy}
              onApply={applyFlow}
            />
          )}

          {error && <div className="text-red-600">{error}</div>}
        </>
      )}
    </Shell>
  );
}

/** The derived totals for the whole planting. */
function DerivedSummary({ derived }: { derived: DerivedFlow }) {
  const rows: [string, string][] = [];
  if (derived.emitters_per_m2 !== null) {
    rows.push(['Emitters', `${derived.emitters_per_m2.toFixed(3)} per m²`]);
  }
  if (derived.emitter_count !== null) {
    rows.push(['Count', `${derived.emitter_count.toLocaleString()} in the planting`]);
  }
  if (derived.lateral_spacing_m !== null) {
    rows.push([
      'Lateral spacing',
      `${derived.lateral_spacing_m} m${derived.lateral_spacing_source === 'row_spacing' ? ' (from the rows)' : ''}`,
    ]);
  }
  if (derived.flow_lpm !== null) {
    rows.push(['Whole planting', `${derived.flow_lpm.toLocaleString()} L/min`]);
  }
  if (derived.flow_lph !== null) {
    rows.push(['', `${derived.flow_lph.toLocaleString()} L/h`]);
  }
  if (derived.application_rate_mm_h !== null) {
    rows.push([
      'Application rate',
      `${derived.application_rate_mm_h.toFixed(2)} mm/h${derived.wets_full_surface ? '' : ' over the block'}`,
    ]);
  }

  return (
    <div className="border rounded bg-white p-2 space-y-1">
      <div className="font-semibold text-gray-700">Worked out from the emitters</div>
      {rows.length === 0 && (
        <div className="text-gray-500">Not enough entered yet to work anything out.</div>
      )}
      <dl className="space-y-0.5">
        {rows.map(([k, v], i) => (
          <div key={i} className="flex justify-between gap-2">
            <dt className="text-gray-500 min-w-0 break-words">{k}</dt>
            <dd className="font-medium text-right break-words">{v}</dd>
          </div>
        ))}
      </dl>
      {!derived.wets_full_surface && derived.application_rate_mm_h !== null && (
        // Drip wets a fraction of the surface, so this figure is a block
        // average for a water budget, not the depth arriving at a tree.
        <div className="text-[11px] text-gray-500">
          This system does not wet the whole surface, so the mm/h figure is an
          average across the block rather than what lands at each tree.
        </div>
      )}
      {derived.warnings.map((w) => (
        <div key={w.code} className="text-amber-700">{w.message}</div>
      ))}
    </div>
  );
}

/**
 * This valve's share of the planting, and the flow that follows from it.
 *
 * `served_area_ha` is edited with the rest of the node (it is a column on it),
 * so this shows the value and points at the field above rather than carrying a
 * second input that could be saved out of step with the drawer.
 */
function ValveShare({
  node, demand, editable, busy, onApply,
}: {
  node: IrrigationNode;
  demand: ValveDemandRow | null;
  editable: boolean;
  busy: boolean;
  onApply: () => void;
}) {
  if (!demand) return null;

  const basisText = demand.served_area_basis === 'stated'
    ? 'as stated on this valve'
    : demand.served_area_basis === 'whole_area'
      ? 'the whole planting — it is the only valve on it'
      : 'an even share of what the other valves leave';

  const reasonText: Record<string, string> = {
    no_spec: 'Nothing is recorded as installed in this planting yet, so there is no flow to derive.',
    incomplete_spec: 'The installed system is not described fully enough to work out a flow yet.',
    no_area: 'This valve has no planting attributed to it.',
    zero_share: 'The other valves on this planting already account for all of it.',
  };

  return (
    <div className="border rounded bg-white p-2 space-y-1">
      <div className="font-semibold text-gray-700">This valve&rsquo;s share</div>

      {demand.served_area_ha !== null ? (
        <div className="text-gray-600">
          Waters <span className="font-medium">{demand.served_area_ha} ha</span> — {basisText}.
        </div>
      ) : (
        <div className="text-gray-500">No share could be worked out.</div>
      )}

      {demand.reason !== 'ok' && (
        <div className="text-amber-700">{reasonText[demand.reason]}</div>
      )}

      {demand.warnings.map((w) => (
        <div key={w.code} className="text-amber-700">{w.message}</div>
      ))}

      {demand.derived_flow_lpm !== null && (
        <>
          <div className="flex justify-between gap-2">
            <span className="text-gray-500">Derived flow</span>
            <span className="font-medium">{demand.derived_flow_lpm.toLocaleString()} L/min</span>
          </div>
          <div className="flex justify-between gap-2">
            <span className="text-gray-500">On the valve now</span>
            <span className="font-medium">
              {demand.current_flow_lpm !== null
                ? `${demand.current_flow_lpm.toLocaleString()} L/min`
                : 'nothing set'}
            </span>
          </div>

          {demand.matches_current ? (
            <div className="text-green-700">
              The valve already carries this figure.
            </div>
          ) : editable && (
            <>
              <button
                className="px-2 py-1 rounded bg-blue-600 text-white disabled:opacity-40 flex items-center gap-1"
                disabled={busy}
                onClick={onApply}
              >
                <Wand2 className="w-3 h-3" /> Use as this valve&rsquo;s rated flow
              </button>
              {demand.current_flow_lpm !== null && (
                // The stored figure may have been read off a flow meter, which
                // beats the arithmetic. Applying is a decision, not a default.
                <div className="text-[11px] text-gray-500">
                  This replaces the {demand.current_flow_lpm.toLocaleString()} L/min
                  already recorded. Keep that one if it was measured at the valve.
                </div>
              )}
            </>
          )}
        </>
      )}

      {toNum(node.served_area_ha) === null && demand.served_area_basis !== 'whole_area' && (
        <div className="text-[11px] text-gray-500">
          Set &ldquo;Waters (ha)&rdquo; above to state this valve&rsquo;s share
          instead of splitting the planting evenly.
        </div>
      )}
    </div>
  );
}

function Shell({ children }: { children: React.ReactNode }) {
  return (
    <div className="mt-2 border rounded bg-gray-50 text-xs">
      <div className="px-2 py-1.5 border-b font-semibold flex items-center gap-1">
        <Droplets className="w-3 h-3" /> Installed irrigation
      </div>
      <div className="p-2 space-y-2">{children}</div>
    </div>
  );
}

function Field({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <div>
      <div className="text-gray-600 mb-0.5">{label}</div>
      {children}
    </div>
  );
}
